import { useCallback, useMemo, useState } from "react";
import { Input, InputNumber, Modal, Switch, Table, message } from "antd";
import type { TableColumnsType } from "antd";
import { Pencil, Plus, Trash2 } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";

import { useGetPlanReviewSubmissionTypesQuery } from "graphql/queries/get-plan-review-submission-types.generated";
import { useGetAhjFeeSchedulesAdminQuery } from "graphql/queries/get-ahj-fee-schedules-admin.generated";
import { useInsertAhjFeeScheduleMutation } from "graphql/mutations/insert-ahj-fee-schedule.generated";
import { useUpdateAhjFeeScheduleMutation } from "graphql/mutations/update-ahj-fee-schedule.generated";
import { useDeleteAhjFeeScheduleMutation } from "graphql/mutations/delete-ahj-fee-schedule.generated";
import { useInsertAhjFeeScheduleVariableMutation } from "graphql/mutations/insert-ahj-fee-schedule-variable.generated";
import { useUpdateAhjFeeScheduleVariableMutation } from "graphql/mutations/update-ahj-fee-schedule-variable.generated";
import { useDeleteAhjFeeScheduleVariableMutation } from "graphql/mutations/delete-ahj-fee-schedule-variable.generated";

import { usePlanReviewJurisdiction } from "hooks/usePlanReviewJurisdiction";
import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

type Schedule = NonNullable<ReturnType<typeof useGetAhjFeeSchedulesAdminQuery>["data"]>["ahj_fee_schedules"][number];

interface VariableFormValue {
	id?: string;
	variable_key: string;
	variable_label: string;
	rate_per_unit: number;
	free_quantity: number;
	is_required: boolean;
}

interface FormValues {
	id?: string;
	submission_type_id: string;
	ahj_base_amount: number;
	ollie_percentage: number;
	ollie_floor_amount: number;
	ollie_ceiling_amount: number | null;
	included_cycles_limit: number;
	extra_resubmittal_price: number;
	active: boolean;
	variables: VariableFormValue[];
}

const EMPTY: FormValues = {
	submission_type_id: "",
	ahj_base_amount: 0,
	ollie_percentage: 10,
	ollie_floor_amount: 0,
	ollie_ceiling_amount: null,
	included_cycles_limit: 3,
	extra_resubmittal_price: 49,
	active: true,
	variables: []
};

const toVariableKey = (label: string): string =>
	label
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

/**
 * What Ollie charges an applicant to pre-screen a plan review, per submission type
 * (MWA-788). A submission type with no schedule here stays free — nothing changes for
 * it until a manager sets one up.
 */
const PlanReviewFeeSchedule = () => {
	const { jurisdictionId, picker, emptyReason, isPlatformAdmin } = usePlanReviewJurisdiction();
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The Ollie fee schedule screen: what an applicant is charged to pre-screen a plan review with this authority, per submission type. The reader configures it here.",
			jurisdictionId
		})
	);

	const { canManage, blockChange, notice } = usePlanReviewSettingsAccess();
	const lockedClass = canManage ? "" : " pafs-locked";

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [values, setValues] = useState<FormValues>(EMPTY);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const { data: submissionTypesData } = useGetPlanReviewSubmissionTypesQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-first"
	});
	const submissionTypes = useMemo(() => submissionTypesData?.plan_review_submission_types ?? [], [submissionTypesData]);

	const {
		data,
		loading,
		error: loadError,
		refetch
	} = useGetAhjFeeSchedulesAdminQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});
	const schedules = useMemo(() => data?.ahj_fee_schedules ?? [], [data]);

	// Only a submission type without a schedule yet can start a new one — editing an
	// existing pair happens from its row instead.
	const availableSubmissionTypes = useMemo(
		() => submissionTypes.filter(type => !schedules.some(s => s.submission_type_id === type.id)),
		[submissionTypes, schedules]
	);

	const [insertSchedule] = useInsertAhjFeeScheduleMutation();
	const [updateSchedule] = useUpdateAhjFeeScheduleMutation();
	const [deleteSchedule] = useDeleteAhjFeeScheduleMutation();
	const [insertVariable] = useInsertAhjFeeScheduleVariableMutation();
	const [updateVariable] = useUpdateAhjFeeScheduleVariableMutation();
	const [deleteVariable] = useDeleteAhjFeeScheduleVariableMutation();

	const openCreate = useCallback(() => {
		if (!canManage) return blockChange();
		setValues({ ...EMPTY, submission_type_id: availableSubmissionTypes[0]?.id ?? "" });
		setError(null);
		setIsModalOpen(true);
	}, [canManage, blockChange, availableSubmissionTypes]);

	const openEdit = useCallback(
		(schedule: Schedule) => {
			if (!canManage) return blockChange();
			setValues({
				id: schedule.id,
				submission_type_id: schedule.submission_type_id,
				ahj_base_amount: Number(schedule.ahj_base_amount),
				ollie_percentage: Number(schedule.ollie_percentage),
				ollie_floor_amount: Number(schedule.ollie_floor_amount),
				ollie_ceiling_amount: schedule.ollie_ceiling_amount == null ? null : Number(schedule.ollie_ceiling_amount),
				included_cycles_limit: schedule.included_cycles_limit,
				extra_resubmittal_price: Number(schedule.extra_resubmittal_price),
				active: schedule.active,
				variables: schedule.ahj_fee_schedule_variables.map(v => ({
					id: v.id,
					variable_key: v.variable_key,
					variable_label: v.variable_label,
					rate_per_unit: Number(v.rate_per_unit),
					free_quantity: Number(v.free_quantity),
					is_required: v.is_required
				}))
			});
			setError(null);
			setIsModalOpen(true);
		},
		[canManage, blockChange]
	);

	const addVariableRow = useCallback(() => {
		setValues(v => ({
			...v,
			variables: [...v.variables, { variable_key: "", variable_label: "", rate_per_unit: 0, free_quantity: 0, is_required: true }]
		}));
	}, []);

	const updateVariableRow = useCallback((index: number, patch: Partial<VariableFormValue>) => {
		setValues(v => ({
			...v,
			variables: v.variables.map((variable, i) =>
				i === index
					? {
							...variable,
							...patch,
							// The key mirrors the label unless someone already typed one in by hand —
							// a fresh row should not force the manager to think of a machine name.
							variable_key:
								patch.variable_label != null && (!variable.variable_key || variable.variable_key === toVariableKey(variable.variable_label))
									? toVariableKey(patch.variable_label)
									: (patch.variable_key ?? variable.variable_key)
						}
					: variable
			)
		}));
	}, []);

	const removeVariableRow = useCallback((index: number) => {
		setValues(v => ({ ...v, variables: v.variables.filter((_, i) => i !== index) }));
	}, []);

	const handleSave = useCallback(async () => {
		if (!values.submission_type_id || !jurisdictionId) return;
		setIsSaving(true);
		setError(null);
		try {
			const scheduleFields = {
				ahj_base_amount: values.ahj_base_amount,
				ollie_percentage: values.ollie_percentage,
				ollie_floor_amount: values.ollie_floor_amount,
				ollie_ceiling_amount: values.ollie_ceiling_amount,
				included_cycles_limit: values.included_cycles_limit,
				extra_resubmittal_price: values.extra_resubmittal_price,
				active: values.active
			};

			let scheduleId = values.id;
			if (scheduleId) {
				await updateSchedule({ variables: { id: scheduleId, set: scheduleFields } });
			} else {
				const created = await insertSchedule({
					variables: { object: { ...scheduleFields, jurisdiction_id: jurisdictionId, submission_type_id: values.submission_type_id } }
				});
				scheduleId = created.data?.insert_ahj_fee_schedules_one?.id;
			}
			if (!scheduleId) throw new Error("Could not save the fee schedule.");

			// Variables have no diffing to speak of: existing ones update in place, blank
			// ids insert, and whatever the original schedule had that this form no longer
			// lists gets deleted.
			const existingSchedule = schedules.find(s => s.id === scheduleId);
			const originalVariableIds = new Set(existingSchedule?.ahj_fee_schedule_variables.map(v => v.id) ?? []);
			const keptVariableIds = new Set(values.variables.filter(v => v.id).map(v => v.id));

			for (const removedId of originalVariableIds) {
				if (!keptVariableIds.has(removedId)) await deleteVariable({ variables: { id: removedId! } });
			}
			for (const [index, variable] of values.variables.entries()) {
				const fields = {
					variable_key: variable.variable_key || toVariableKey(variable.variable_label),
					variable_label: variable.variable_label,
					rate_per_unit: variable.rate_per_unit,
					free_quantity: variable.free_quantity,
					is_required: variable.is_required,
					sort_order: index
				};
				if (variable.id) {
					await updateVariable({ variables: { id: variable.id, set: fields } });
				} else {
					await insertVariable({ variables: { object: { ...fields, ahj_fee_schedule_id: scheduleId } } });
				}
			}

			await refetch();
			setIsModalOpen(false);
			message.success(values.id ? "Fee schedule updated." : "Fee schedule created.");
		} catch (saveError) {
			console.error("Fee schedule save failed:", saveError);
			const detail = saveError instanceof Error ? saveError.message : "";
			setError(`We couldn't save the fee schedule. ${detail}`.trim());
		} finally {
			setIsSaving(false);
		}
	}, [values, jurisdictionId, schedules, insertSchedule, updateSchedule, insertVariable, updateVariable, deleteVariable, refetch]);

	const handleDelete = useCallback(
		async (schedule: Schedule) => {
			if (!canManage) return blockChange();
			try {
				const result = await deleteSchedule({ variables: { id: schedule.id } });
				if (!result.data?.delete_ahj_fee_schedules_by_pk) {
					message.error("We couldn't delete that fee schedule.");
					return;
				}
				await refetch();
				message.success("Fee schedule deleted. This submission type is free again.");
			} catch (deleteError) {
				console.error("Fee schedule delete failed:", deleteError);
				message.error("We couldn't delete the fee schedule. Please try again.");
			}
		},
		[deleteSchedule, refetch, canManage, blockChange]
	);

	const columns: TableColumnsType<Schedule> = useMemo(
		() => [
			{
				title: "Submission type",
				key: "submission_type",
				render: (_, schedule) => schedule.submission_type.name
			},
			{
				title: "AHJ base fee",
				key: "ahj_base_amount",
				render: (_, schedule) => `$${Number(schedule.ahj_base_amount).toFixed(2)}`
			},
			{
				title: "Variables",
				key: "variables",
				render: (_, schedule) => schedule.ahj_fee_schedule_variables.length
			},
			{
				title: "Ollie %",
				key: "ollie_percentage",
				render: (_, schedule) => `${Number(schedule.ollie_percentage)}%`
			},
			{
				title: "Floor / ceiling",
				key: "floor_ceiling",
				render: (_, schedule) =>
					`$${Number(schedule.ollie_floor_amount).toFixed(2)} – ${
						schedule.ollie_ceiling_amount == null ? "no ceiling" : `$${Number(schedule.ollie_ceiling_amount).toFixed(2)}`
					}`
			},
			{
				title: "Included resubmittals",
				dataIndex: "included_cycles_limit",
				key: "included_cycles_limit"
			},
			{
				title: "Extra resubmittal",
				key: "extra_resubmittal_price",
				render: (_, schedule) => `$${Number(schedule.extra_resubmittal_price).toFixed(2)}`
			},
			{
				title: "Active",
				dataIndex: "active",
				key: "active",
				render: (active: boolean) => (active ? "Yes" : "No")
			},
			{
				title: "",
				key: "actions",
				width: 90,
				render: (_, schedule) => (
					<div className="pafs-cell-actions">
						<button
							type="button"
							className={`pafs-icon-btn${lockedClass}`}
							title="Edit"
							aria-disabled={!canManage}
							onClick={() => openEdit(schedule)}
						>
							<Pencil size={15} />
						</button>
						<button
							type="button"
							className={`pafs-icon-btn${lockedClass}`}
							title="Delete"
							aria-disabled={!canManage}
							onClick={() => handleDelete(schedule)}
						>
							<Trash2 size={15} />
						</button>
					</div>
				)
			}
		],
		[openEdit, handleDelete, canManage, lockedClass]
	);

	return (
		<>
			<OllieLayout>
				<div className="pafs-page">
					<div className="pafs-shell">
						<header className="pafs-header">
							<div>
								<h1 className="pafs-title">Ollie Fee Schedule</h1>
								<p className="pafs-subtitle">
									What an applicant pays to pre-screen a plan review with your jurisdiction. A submission type with no
									schedule stays free.
								</p>
							</div>
							<div className="pafs-header-actions">
								<button
									className={`pafs-btn pafs-btn-primary${lockedClass}`}
									onClick={openCreate}
									aria-disabled={!canManage}
									disabled={!jurisdictionId || availableSubmissionTypes.length === 0}
								>
									<Plus size={16} />
									Add fee schedule
								</button>
							</div>
						</header>

						{isPlatformAdmin ? <div className="pafs-header">{picker}</div> : null}

						{notice}

						{loadError ? (
							<div className="pafs-empty">
								<p className="pafs-empty-title">We couldn&apos;t load the fee schedules</p>
								<p>{loadError.message}</p>
							</div>
						) : emptyReason ? (
							<div className="pafs-empty">
								<p className="pafs-empty-title">{emptyReason.title}</p>
								<p>{emptyReason.detail}</p>
							</div>
						) : !loading && schedules.length === 0 ? (
							<div className="pafs-empty">
								<p className="pafs-empty-title">No fee schedules yet</p>
								<p>Every submission type is free until you add one here.</p>
							</div>
						) : (
							<Table
								className="pafs-table"
								rowKey="id"
								columns={columns}
								dataSource={schedules}
								loading={loading && schedules.length === 0}
								pagination={false}
							/>
						)}
					</div>
				</div>
			</OllieLayout>

			<Modal
				open={isModalOpen}
				onCancel={() => setIsModalOpen(false)}
				footer={null}
				closable={false}
				width={720}
				className="pafs-modal"
			>
				<h2 className="pafs-modal-title">{values.id ? "Edit fee schedule" : "Add fee schedule"}</h2>

				<div className="pafs-form">
					<div className="pafs-form-field">
						<label className="pafs-label">Submission type</label>
						<Input
							className="pafs-control"
							value={
								values.id
									? submissionTypes.find(t => t.id === values.submission_type_id)?.name
									: (submissionTypes.find(t => t.id === values.submission_type_id)?.name ?? "")
							}
							disabled
						/>
						{!values.id && (
							<select
								className="pafs-native-select"
								value={values.submission_type_id}
								onChange={e => setValues(v => ({ ...v, submission_type_id: e.target.value }))}
							>
								{availableSubmissionTypes.map(type => (
									<option key={type.id} value={type.id}>
										{type.name}
									</option>
								))}
							</select>
						)}
					</div>

					<div className="pafs-form-field">
						<label className="pafs-label">AHJ base fee ($)</label>
						<InputNumber
							className="pafs-control"
							min={0}
							value={values.ahj_base_amount}
							onChange={value => setValues(v => ({ ...v, ahj_base_amount: value ?? 0 }))}
						/>
						<p className="pafs-hint">The AHJ&apos;s own flat fee for this submission type, before any variable is added.</p>
					</div>

					<div className="pafs-form-field">
						<label className="pafs-label">Ollie fee (% of AHJ price)</label>
						<InputNumber
							className="pafs-control"
							min={0}
							max={100}
							value={values.ollie_percentage}
							onChange={value => setValues(v => ({ ...v, ollie_percentage: value ?? 0 }))}
						/>
					</div>

					<div className="pafs-form-field">
						<label className="pafs-label">Floor ($)</label>
						<InputNumber
							className="pafs-control"
							min={0}
							value={values.ollie_floor_amount}
							onChange={value => setValues(v => ({ ...v, ollie_floor_amount: value ?? 0 }))}
						/>
					</div>

					<div className="pafs-form-field">
						<label className="pafs-label">Ceiling ($, optional)</label>
						<InputNumber
							className="pafs-control"
							min={0}
							value={values.ollie_ceiling_amount ?? undefined}
							onChange={value => setValues(v => ({ ...v, ollie_ceiling_amount: value ?? null }))}
						/>
					</div>

					<div className="pafs-form-field">
						<label className="pafs-label">Included resubmittals</label>
						<InputNumber
							className="pafs-control"
							min={0}
							value={values.included_cycles_limit}
							onChange={value => setValues(v => ({ ...v, included_cycles_limit: value ?? 0 }))}
						/>
					</div>

					<div className="pafs-form-field">
						<label className="pafs-label">Extra resubmittal price ($)</label>
						<InputNumber
							className="pafs-control"
							min={0}
							value={values.extra_resubmittal_price}
							onChange={value => setValues(v => ({ ...v, extra_resubmittal_price: value ?? 0 }))}
						/>
					</div>

					<div className="pafs-form-field pafs-form-field-full">
						<label className="pafs-label">Active?</label>
						<Switch checked={values.active} onChange={checked => setValues(v => ({ ...v, active: checked }))} />
					</div>
				</div>

				<div className="pafs-variables-editor">
					<div className="pafs-variables-header">
						<h3 className="pafs-variables-title">Variables</h3>
						<button type="button" className="pafs-btn" onClick={addVariableRow}>
							<Plus size={14} />
							Add variable
						</button>
					</div>
					<p className="pafs-hint">
						What the applicant is asked for on the plan-review-add screen (square footage, device count, etc). Leave empty
						if this submission type only needs the AHJ base fee above.
					</p>

					{values.variables.length > 0 && (
						<div className="pafs-variable-rows">
							{/* One shared grid for the header and every row below: two separate grids with
							    the same grid-template-columns still size their auto/fr tracks independently,
							    so the header text drifts from the inputs once a row's toggle or trash-icon
							    column is a different width than the (empty) header cell above it. */}
							<div className="pafs-variable-row pafs-variable-row-header">
								<span className="pafs-variable-col-label">Label</span>
								<span className="pafs-variable-col-label">Cost per unit</span>
								<span className="pafs-variable-col-label">Qty included in base fee</span>
								<span />
								<span />
							</div>

							{values.variables.map((variable, index) => (
								<div className="pafs-variable-row" key={index}>
									<Input
										className="pafs-control"
										placeholder="Label shown to the applicant, e.g. Number of fire alarm devices"
										value={variable.variable_label}
										onChange={e => updateVariableRow(index, { variable_label: e.target.value })}
									/>
									<InputNumber
										className="pafs-control"
										placeholder="Rate per unit"
										min={0}
										value={variable.rate_per_unit}
										onChange={value => updateVariableRow(index, { rate_per_unit: value ?? 0 })}
									/>
									<InputNumber
										className="pafs-control"
										placeholder="Free quantity"
										min={0}
										value={variable.free_quantity}
										onChange={value => updateVariableRow(index, { free_quantity: value ?? 0 })}
									/>
									<label className="pafs-variable-required">
										<Switch
											size="small"
											checked={variable.is_required}
											onChange={checked => updateVariableRow(index, { is_required: checked })}
										/>
										Required
									</label>
									<button
										type="button"
										className="pafs-icon-btn"
										title="Remove"
										onClick={() => removeVariableRow(index)}
									>
										<Trash2 size={15} />
									</button>
								</div>
							))}
						</div>
					)}
				</div>

				{error ? <p className="pafs-error">{error}</p> : null}

				<div className="pafs-modal-actions">
					<button type="button" className="pafs-btn" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
						Cancel
					</button>
					<button
						type="button"
						className="pafs-btn pafs-btn-primary"
						onClick={handleSave}
						disabled={isSaving || !values.submission_type_id}
					>
						{isSaving ? "Saving…" : "Save schedule"}
					</button>
				</div>
			</Modal>

			<LoadingOverlay isLoading={isSaving} tip="Saving…" />
		</>
	);
};

export default PlanReviewFeeSchedule;
